import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { usePlayer, usePlayers, useResetHandle } from "../api/queries";
import type { PlayerDetail } from "../api/schemas";
import { useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import {
  Button,
  Card,
  CategoryTag,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Skeleton,
  Stat,
  TextInput,
} from "../components/ui";
import { addDays, formatDateTime, formatShortDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtMs, shortId } from "../lib/format";
import { ORB_NAMES, Orb } from "../components/Orb";
import styles from "./pages.module.css";
import { PlayerPhase2 } from "./PlayerPhase2";
import { PlayerTabs } from "./revenue/PlayerTabs";

export function PlayersPage() {
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("q") ?? "");
  const q = params.get("q") ?? "";
  const players = usePlayers(q);
  const navigate = useNavigate();

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (draft !== q) setParams(draft ? { q: draft } : {}, { replace: true });
    }, 250);
    return () => window.clearTimeout(t);
  }, [draft, q, setParams]);

  return (
    <>
      <PageHeader title="Players" meta={<span>Search by @handle, invite code (both shown on the player's Profile), userId prefix or exact installId.</span>} />
      <div className={styles.filters}>
        <Field label="Search">
          <TextInput
            data-search
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="@handle, K7QX2M, 3f9a… or inst_…   ( / )"
            style={{ width: 340 }}
            autoFocus
          />
        </Field>
        <span className={styles.filtersEnd}>
          <span className="monoLabel">{q ? "matches" : "newest players"}</span>
        </span>
      </div>

      {players.isError ? (
        <ErrorState error={players.error} onRetry={() => void players.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={players.isFetching}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th aria-label="Orb" />
                <th>Handle</th>
                <th>User id</th>
                <th>Install id</th>
                <th>Created</th>
                <th data-align="right">Streak</th>
                <th>Last played</th>
              </tr>
            </thead>
            <tbody>
              {players.isPending
                ? Array.from({ length: 8 }, (_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }, (__, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={j === 0 ? 16 : j === 1 ? 110 : j < 4 ? 200 : 70} />
                        </td>
                      ))}
                    </tr>
                  ))
                : players.data.items.map((p) => (
                    <tr
                      key={p.userId}
                      data-clickable
                      onClick={(e) => !(e.target as HTMLElement).closest("a") && navigate(`/players/${p.userId}`)}
                    >
                      <td>{p.orb !== null ? <Orb index={p.orb} size={16} label={`Orb ${p.orb} · ${ORB_NAMES[p.orb]}`} /> : null}</td>
                      <td>{p.handle ? `@${p.handle}` : <span className={tableStyles.faint}>—</span>}</td>
                      <td>
                        <Link to={`/players/${p.userId}`} className={tableStyles.id}>
                          {p.userId}
                        </Link>
                      </td>
                      <td className={`${tableStyles.mono} ${tableStyles.dim}`}>{p.installId}</td>
                      <td className={tableStyles.mono}>{formatDateTime(p.createdAt)}</td>
                      <td data-align="right" className="mono">
                        {p.streak ? `${p.streak}d` : <span className={tableStyles.faint}>0</span>}
                      </td>
                      <td className={tableStyles.mono}>{p.lastPlayedDate ? formatShortDate(p.lastPlayedDate) : "—"}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
          {!players.isPending && players.data.items.length === 0 ? (
            <EmptyState title="No players found">No handle or userId starts with “{q}”, and no invite code or installId equals it.</EmptyState>
          ) : null}
        </div>
      )}
    </>
  );
}

export function PlayerDetailPage() {
  const { id = "" } = useParams();
  const q = usePlayer(id);
  const header = (
    <>
      <PageHeader
        title={<span className="mono">{shortId(id, 13)}</span>}
        meta={
          <>
            <Link to="/players" className={styles.back}>
              ← Players
            </Link>
            <span className="mono">{id}</span>
          </>
        }
      />
      {/* Profile | Subscription (contracts/revenue.md §5), with the subscription status on its tab. */}
      <PlayerTabs id={id} />
    </>
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState
          error={q.error}
          onRetry={() => void q.refetch()}
          title={(q.error as { status?: number }).status === 404 ? "No such player" : "Couldn't load this player"}
        />
      </>
    );
  const d = q.data;
  return (
    <>
      {header}
      <div className={styles.playerTop}>
        <Card title="Profile" actions={<ResetHandle player={d.player} />}>
          <dl className={styles.kv}>
            <dt>Handle</dt>
            <dd data-testid="player-handle">
              {d.player.handle ? <span className="mono">@{d.player.handle}</span> : <span className={styles.faint}>not set</span>}
            </dd>
            <dt>Orb</dt>
            <dd data-testid="player-orb">
              {d.player.orb !== null ? (
                <span className={styles.orbValue}>
                  <Orb index={d.player.orb} size={18} />
                  <span className="mono">
                    {d.player.orb} · {ORB_NAMES[d.player.orb]}
                  </span>
                </span>
              ) : (
                <span className={styles.faint}>not reported</span>
              )}
            </dd>
            <dt>Team</dt>
            <dd data-testid="player-team">
              {d.team ? (
                <>
                  <Link to={`/teams/${d.team.id}`}>{d.team.name}</Link>
                  {d.team.role === "captain" ? <span className={styles.faint}> · captain</span> : null}
                </>
              ) : (
                <span className={styles.faint}>none</span>
              )}
            </dd>
            <dt>Sign-in</dt>
            <dd>
              <span className={styles.chips} data-testid="player-identities">
                {d.player.identities.length ? (
                  d.player.identities.map((p) => (
                    <Chip key={p} tone="info">
                      {p === "apple" ? "Apple" : p === "google" ? "Google" : p}
                    </Chip>
                  ))
                ) : (
                  <Chip tone="muted">anonymous</Chip>
                )}
              </span>
            </dd>
            <dt>Install</dt>
            <dd className="mono">{d.player.installId}</dd>
            <dt>Created</dt>
            <dd className="mono">{formatDateTime(d.player.createdAt)}</dd>
            <dt>Last played</dt>
            <dd className="mono">{d.player.lastPlayedDate ? formatShortDate(d.player.lastPlayedDate) : "—"}</dd>
          </dl>
        </Card>
        <Card title="Streak">
          <div className={styles.statsRow}>
            <Stat label="Current" value={`${d.streak.current}d`} />
            <Stat label="Best" value={`${d.streak.best}d`} />
            <Stat label="Played (30d)" value={d.daily.length} sub={`${d.daily.filter((a) => a.correct).length} correct`} />
          </div>
          <AttemptStrip attempts={d.daily} />
        </Card>
        <Card title="Flags" actions={d.flags.length ? <Chip tone="danger">{d.flags.length}</Chip> : <Chip tone="success">clean</Chip>}>
          {d.flags.length === 0 ? (
            <p className={styles.faint}>No flagged submissions in the last 30 days.</p>
          ) : (
            <ul className={styles.flagList}>
              {d.flags.map((f, i) => (
                <li key={i}>
                  <Link to={`/integrity?date=${f.date}`} className="mono">
                    #{f.puzzleNumber}
                  </Link>
                  {f.flags.map((x) => (
                    <Chip key={x} tone="danger">
                      {x.replace("_", " ")}
                    </Chip>
                  ))}
                  <span className="mono">{fmtMs(f.elapsedMs)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <PlayerPhase2 detail={d} />

      <div className={styles.playerBottom}>
        <Card title="Last 30 daily attempts" padded={false}>
          {d.daily.length === 0 ? (
            <EmptyState title="No daily attempts">This player hasn't submitted a daily puzzle in the last 30 days.</EmptyState>
          ) : (
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th data-align="right">#</th>
                  <th>Result</th>
                  <th data-align="right">Score</th>
                  <th data-align="right">Time</th>
                  <th data-align="right">Percentile</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {d.daily.map((a) => (
                  <tr key={a.date}>
                    <td className={tableStyles.mono}>{formatShortDate(a.date)}</td>
                    <td data-align="right" className={`${tableStyles.mono} ${tableStyles.dim}`}>
                      {a.puzzleNumber}
                    </td>
                    <td>{a.correct ? <Chip tone="success">correct</Chip> : <Chip tone="danger">wrong</Chip>}</td>
                    <td data-align="right" className="mono">
                      {a.score}
                    </td>
                    <td data-align="right" className="mono">
                      {fmtMs(a.elapsedMs)}
                    </td>
                    <td data-align="right" className="mono">
                      {a.percentile === null ? "—" : `${Math.round(a.percentile)}th`}
                    </td>
                    <td>
                      <span className={styles.chips}>
                        {a.flags.map((f) => (
                          <Chip key={f} tone={f === "too_fast" ? "danger" : "warning"}>
                            {f.replace("_", " ")}
                          </Chip>
                        ))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card title="Level progress">
          {d.levels.length === 0 ? (
            <p className={styles.faint}>No levels played.</p>
          ) : (
            <div className={styles.progressList}>
              {d.levels.map((l) => {
                const max = Math.max(...d.levels.map((x) => x.highestLevel), 1);
                return (
                  <div key={l.track} className={styles.progressRow}>
                    <CategoryTag category={l.track} />
                    <span className={styles.meter} data-cat={l.track}>
                      <span style={{ width: `${(l.highestLevel / max) * 100}%` }} />
                    </span>
                    <span className="mono">L{fmtInt(l.highestLevel)}</span>
                    <span className={`mono ${styles.faint}`}>{fmtInt(l.stars)}★</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/** "Reset handle" (editor+): clears an offensive handle after a confirm step; the API audits it. */
function ResetHandle({ player }: { player: PlayerDetail["player"] }) {
  const allowed = useCan("player.moderate");
  const [open, setOpen] = useState(false);
  const mutation = useResetHandle();
  if (!allowed || !player.handle) return null;
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          mutation.reset();
          setOpen(true);
        }}
      >
        Reset handle
      </Button>
      <ConfirmDialog
        open={open}
        title="Reset this handle?"
        confirmLabel="Reset handle"
        tone="danger"
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setOpen(false)}
        onConfirm={() => mutation.mutate(player.userId, { onSuccess: () => setOpen(false) })}
      >
        <p className={styles.prose}>
          <span className="mono">@{player.handle}</span> is removed from this player's profile and share cards. They can pick a new
          handle in the app. The reset is written to the audit log.
        </p>
      </ConfirmDialog>
    </>
  );
}

/** 30 cells, oldest → newest, like the app's share grid. */
function AttemptStrip({ attempts }: { attempts: PlayerDetail["daily"] }) {
  const today = todayUtc();
  const byDate = new Map(attempts.map((a) => [a.date, a]));
  return (
    <div className={styles.strip} role="img" aria-label="Daily results for the last 30 days">
      {Array.from({ length: 30 }, (_, i) => {
        const date = addDays(today, i - 29);
        const a = byDate.get(date);
        return <span key={date} data-state={a ? (a.correct ? "correct" : "wrong") : "missed"} title={`${formatShortDate(date)}: ${a ? (a.correct ? `correct · ${a.score}` : "wrong") : "not played"}`} />;
      })}
    </div>
  );
}
